/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, hasInjectionContext, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';

import { isMfaRequiredSentinelError, isPendingLivenessCheckSentinelError } from '@peanuds/shared';
import {
  isZodError,
  reportBackendFailure,
  reportZodValidationError,
} from '@peanuds/shared/observability/runtime-observability';
import { getRequestErrorDetail, getRequestErrorSummary } from '../request-error-toast-copy';

/**
 * Extracts the success response data type from the hey-api SDK's
 * discriminated union: { data: T; error: undefined } | { data: undefined; error: E }
 */
type ExtractSuccessData<T> = T extends { data: infer D; error: undefined } ? D : never;

/**
 * Infers the response data type from an SDK request function's return type.
 */
type ApiResponseData<TRequest extends (...args: any[]) => Promise<any>> = ExtractSuccessData<
  Awaited<ReturnType<TRequest>>
>;

type ExtractSuccessResponse<T> = T extends { error: undefined } ? T : never;

type ApiSuccessResponse<TRequest extends (...args: any[]) => Promise<any>> = ExtractSuccessResponse<
  Awaited<ReturnType<TRequest>>
>;

type ApiResponseHeaders<TRequest extends (...args: any[]) => Promise<any>> =
  ApiSuccessResponse<TRequest> extends { headers?: infer THeaders } ? THeaders : undefined;

type RelaxHeaders<TParam> = TParam extends { headers: infer THeaders }
  ? Omit<TParam, 'headers'> & { headers?: Partial<THeaders> }
  : TParam;

type RelaxedParams<TRequest extends (...args: any[]) => Promise<any>> =
  Parameters<TRequest> extends [infer TFirst, ...infer TRest]
    ? [RelaxHeaders<TFirst>, ...TRest]
    : Parameters<TRequest>;

type State =
  | { state: 'IDLE' }
  | { state: 'LOADING' }
  | { state: 'SUCCESS' }
  | { state: 'ERROR'; error: unknown };

type ConcurrencyStrategy = 'parallel' | 'latest-wins';

const SDK_OPTION_KEYS = new Set(['body', 'headers', 'path', 'query', 'signal', 'throwOnError']);

const isSdkOptionsObject = (value: object) => {
  const keys = Object.keys(value);

  return keys.length === 0 || keys.some((key) => SDK_OPTION_KEYS.has(key));
};

/**
 * State machine composable for PeanudsApi SDK methods.
 *
 * Handles the SDK's discriminated union response pattern ({ data, error })
 * and provides reactive state management (IDLE → LOADING → SUCCESS / ERROR).
 *
 * @example
 * ```ts
 * // Query — pre-bound params (no args exposed to caller)
 * const {
 *   execute: fetchBeneficiaries,
 *   data: beneficiaries,
 *   isLoading,
 *   error,
 * } = useApiRequest({
 *   request: () => peanudsApi.listBeneficiaries({ query: { limit: 100 } }),
 * });
 *
 * // Mutation — params passed through
 * const {
 *   execute: createBeneficiary,
 *   data: createdBeneficiary,
 *   isLoading: isCreating,
 *   error: createError,
 * } = useApiRequest({
 *   request: (options: Options<CreateIndividualBeneficiaryData>) =>
 *     peanudsApi.createIndividualBeneficiary(options),
 * });
 * ```
 */
export const useApiRequest = <TRequest extends (...args: any[]) => Promise<any>>(config: {
  request: TRequest;
  applyAuthHeaders?: boolean;
  showErrorToast?: boolean;
  concurrency?: {
    strategy?: ConcurrencyStrategy;
  };
}) => {
  type TData = ApiResponseData<TRequest>;
  type TResponse = ApiSuccessResponse<TRequest>;
  type TParams = Parameters<TRequest>;
  type TExecuteParams = RelaxedParams<TRequest>;

  const concurrencyStrategy = config.concurrency?.strategy ?? 'parallel';
  const toast = (
    !hasInjectionContext()
      ? null
      : (() => {
          try {
            return useToast();
          } catch {
            return null;
          }
        })()
  ) as ReturnType<typeof useToast> | null;
  const state = ref<State>({ state: 'IDLE' });
  const data = ref<TData>() as Ref<TData | undefined>;
  const response = ref<TResponse>() as Ref<TResponse | undefined>;
  let latestExecutionId = 0;
  let invalidationVersion = 0;
  const requestName = config.request.name.length > 0 ? config.request.name : 'anonymous-request';

  const logRequestError = (error: unknown) => {
    console.error('[useApiRequest] Error in useApiRequest for request:', config.request, error);
  };

  const reportApiFailure = (error: unknown, operation: string) => {
    if (isPendingLivenessCheckSentinelError(error) || isMfaRequiredSentinelError(error)) {
      return;
    }

    reportBackendFailure({
      error,
      area: 'api',
      operation,
      source: requestName,
    });
  };

  const goToErrorState = async (error: unknown): Promise<void> => {
    state.value = { state: 'ERROR', error };

    if (isPendingLivenessCheckSentinelError(error) || isMfaRequiredSentinelError(error)) {
      logRequestError(error);
      return;
    }

    // TODO: REPLACE WITH GLOBAL ERROR HANDLER
    if (config.showErrorToast !== false) {
      toast?.add({
        severity: 'error',
        summary: getRequestErrorSummary(),
        detail: getRequestErrorDetail('UNEXPECTED ERROR'),
      });
    }
    logRequestError(error);
  };

  const transitions = {
    goToIdleState: (): void => {
      state.value = { state: 'IDLE' };
    },
    goToLoadingState: (): void => {
      state.value = { state: 'LOADING' };
    },
    goToSuccessState: (): void => {
      state.value = { state: 'SUCCESS' };
    },
    goToErrorState,
  } as const;

  const reset = () => {
    transitions.goToIdleState();
    data.value = undefined;
    response.value = undefined;
  };

  const invalidate = () => {
    invalidationVersion += 1;

    if (concurrencyStrategy === 'latest-wins') {
      latestExecutionId += 1;
    }
  };

  const shouldIgnoreResult = (executionId: number, executionInvalidationVersion: number) => {
    if (concurrencyStrategy !== 'latest-wins') {
      return false;
    }

    return (
      executionId !== latestExecutionId || executionInvalidationVersion !== invalidationVersion
    );
  };

  const normalizeRequestParams = async (params: TExecuteParams): Promise<TParams> => {
    if (params.length === 0) {
      return params as unknown as TParams;
    }

    if (config.applyAuthHeaders === false) {
      return params as unknown as TParams;
    }

    const [firstParam, ...restParams] = params;

    if (firstParam !== undefined && (typeof firstParam !== 'object' || firstParam === null)) {
      return params as unknown as TParams;
    }

    if (!isSdkOptionsObject(firstParam ?? {})) {
      return params as unknown as TParams;
    }

    const { withAuthenticatedHeaders } = await import('@peanuds/shared');
    const normalizedFirstParam = await withAuthenticatedHeaders((firstParam ?? {}) as object);

    return [normalizedFirstParam, ...restParams] as unknown as TParams;
  };

  const execute = async (...params: TExecuteParams): Promise<TData | undefined> => {
    const executionId =
      concurrencyStrategy === 'latest-wins' ? ++latestExecutionId : latestExecutionId;
    const executionInvalidationVersion = invalidationVersion;
    transitions.goToLoadingState();

    try {
      const normalizedParams = await normalizeRequestParams(params);

      if (shouldIgnoreResult(executionId, executionInvalidationVersion)) {
        return undefined;
      }

      const result = await config.request(...normalizedParams);

      // The SDK client catches internal errors (Zod validation, network, etc.)
      // and returns the error object directly instead of the { data, error } union.
      // Detect this before attempting discriminated-union handling.
      // On ZodError: warn in dev, still pass the (unvalidated) data through
      if (result instanceof Error) {
        if (isZodError(result)) {
          reportZodValidationError({
            error: result,
            area: 'api',
            operation: 'response-validation',
            source: requestName,
          });
          console.warn(
            '[useApiRequest] Response validation failed for request:',
            config.request,
            result,
          );
          // decide how to handle zod errors like this one- if it's malformed request, that's our problem, if it's malformed response, that's the backend's problem
        } else {
          reportApiFailure(result, 'sdk-error');
        }

        if (shouldIgnoreResult(executionId, executionInvalidationVersion)) {
          return undefined;
        }

        await transitions.goToErrorState(result);
        return undefined;
      }

      // Handle SDK discriminated union: { data, error }
      if (result.error !== undefined) {
        reportApiFailure(result.error, 'api-error-response');

        if (shouldIgnoreResult(executionId, executionInvalidationVersion)) {
          return undefined;
        }

        await transitions.goToErrorState(result.error);
        return undefined;
      }

      if (shouldIgnoreResult(executionId, executionInvalidationVersion)) {
        return undefined;
      }

      response.value = result as TResponse;
      data.value = result.data as TData;
      transitions.goToSuccessState();
      return result.data as TData;
    } catch (error: unknown) {
      // Catch unexpected errors (network failures, interceptor rejections, etc.)
      const errorValue = error instanceof Error ? error : String(error);
      if (isZodError(errorValue)) {
        reportZodValidationError({
          error: errorValue,
          area: 'api',
          operation: 'response-validation',
          source: requestName,
        });
      } else {
        reportApiFailure(errorValue, 'unexpected-exception');
      }

      if (shouldIgnoreResult(executionId, executionInvalidationVersion)) {
        return undefined;
      }

      await transitions.goToErrorState(errorValue);
      return undefined;
    }
  };

  const isIdle = computed(() => state.value.state === 'IDLE');
  const isLoading = computed(() => state.value.state === 'LOADING');
  const isSuccess = computed(() => state.value.state === 'SUCCESS');
  const isError = computed(() => state.value.state === 'ERROR');

  const error = computed(() => {
    if (!isError.value) return;
    if (state.value.state !== 'ERROR') return;

    return state.value.error;
  });

  const headers = computed<ApiResponseHeaders<TRequest> | undefined>(() => {
    const currentResponse = response.value;

    if (!currentResponse || !('headers' in currentResponse)) {
      return undefined;
    }

    return currentResponse.headers as ApiResponseHeaders<TRequest>;
  });

  return {
    state,
    data,
    response,
    headers,
    execute,
    reset,
    invalidate,
    isIdle,
    isLoading,
    isSuccess,
    isError,
    error,
  };
};
